import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Play, 
  Loader2, 
  Terminal, 
  Zap,
  Clock,
  CheckCircle,
  ArrowRight,
  Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SandboxPage = () => {
  const { connectorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [connectors, setConnectors] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const fetchConnectors = async () => {
      try {
        const response = await axios.get(`${API}/connectors?limit=50`);
        setConnectors(response.data);
        
        if (connectorId) {
          const found = response.data.find(c => c.id === connectorId);
          if (found) {
            setSelectedConnector(found);
            // Set a default prompt based on the connector
            if (found.supported_actions?.length > 0) {
              setPrompt(found.supported_actions[0].example_prompt);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching connectors:', error);
      }
    };
    fetchConnectors();
  }, [connectorId]);

  const handleSelectConnector = (connectorId) => {
    const connector = connectors.find(c => c.id === connectorId);
    setSelectedConnector(connector);
    setResult(null);
    if (connector?.supported_actions?.length > 0) {
      setPrompt(connector.supported_actions[0].example_prompt);
    }
  };

  const handleTest = async () => {
    if (!user) {
      toast.error('Please sign in to test connectors');
      return;
    }
    
    if (!selectedConnector) {
      toast.error('Please select a connector');
      return;
    }
    
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setLoading(true);
    setResult(null);
    
    try {
      const response = await axios.post(`${API}/sandbox/test`, {
        connector_id: selectedConnector.id,
        prompt: prompt.trim(),
      });
      
      setResult(response.data);
      setHistory(prev => [{
        connector: selectedConnector.name,
        prompt: prompt.trim(),
        response: response.data,
        timestamp: new Date().toISOString()
      }, ...prev.slice(0, 9)]);
      
      toast.success('Test completed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setLoading(false);
    }
  };

  const formatJson = (obj) => {
    return JSON.stringify(obj, null, 2);
  };

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
            <Terminal className="w-3.5 h-3.5 mr-1.5" />
            Sandbox Environment
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
            Test Connectors
          </h1>
          <p className="text-lg text-slate-400">
            Try connectors in an isolated sandbox before deploying to production
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Panel - Connector Selection & Input */}
          <div className="lg:col-span-2 space-y-6">
            {/* Connector Selection */}
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <h2 className="font-heading font-semibold text-white mb-4">Select Connector</h2>
              <Select 
                value={selectedConnector?.id || ''} 
                onValueChange={handleSelectConnector}
              >
                <SelectTrigger className="bg-secondary border-border" data-testid="connector-select">
                  <SelectValue placeholder="Choose a connector to test" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-80">
                  {connectors.map((connector) => (
                    <SelectItem key={connector.id} value={connector.id}>
                      <div className="flex items-center gap-2">
                        <span>{connector.name}</span>
                        <span className="text-xs text-slate-500">({connector.industry})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedConnector && (
                <div className="mt-4 p-4 bg-secondary/50 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center overflow-hidden">
                      {selectedConnector.icon_url ? (
                        <img src={selectedConnector.icon_url} alt={selectedConnector.name} className="w-full h-full object-cover" />
                      ) : (
                        <Zap className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{selectedConnector.name}</h3>
                      <p className="text-sm text-slate-500">{selectedConnector.category}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400">{selectedConnector.description}</p>
                </div>
              )}
            </div>

            {/* Prompt Input */}
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <h2 className="font-heading font-semibold text-white mb-4">Enter Prompt</h2>
              <div className="space-y-4">
                <Input
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., Show me all open tasks for Project Alpha"
                  className="bg-secondary border-border text-white h-12"
                  onKeyDown={(e) => e.key === 'Enter' && handleTest()}
                  data-testid="sandbox-prompt-input"
                />
                
                {/* Quick Prompts */}
                {selectedConnector?.supported_actions?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Quick prompts:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedConnector.supported_actions.slice(0, 3).map((action, index) => (
                        <button
                          key={index}
                          onClick={() => setPrompt(action.example_prompt)}
                          className="px-3 py-1.5 bg-secondary rounded-lg text-sm text-slate-300 hover:bg-secondary/80 hover:text-white transition-colors"
                        >
                          {action.example_prompt.substring(0, 40)}...
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleTest}
                  disabled={loading || !selectedConnector}
                  className="w-full glow-primary bg-primary hover:bg-primary/90"
                  data-testid="run-test-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Running Test...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      Run Test
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Result */}
            {result && (
              <div className="bg-card border border-white/5 rounded-xl overflow-hidden animate-slide-up">
                <div className="bg-accent/10 border-b border-accent/20 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span className="font-semibold text-white">Test Successful</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Clock className="w-4 h-4" />
                    {result.execution_time_ms}ms
                  </div>
                </div>
                
                <div className="p-6 space-y-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Connector</p>
                    <p className="text-white font-medium">{result.connector_name}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Action Executed</p>
                    <code className="text-primary font-mono">{result.action_executed}</code>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Prompt</p>
                    <p className="text-slate-300">"{result.prompt}"</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Response</p>
                    <div className="sandbox-response bg-slate-950/50 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-slate-300 font-mono">
                        {formatJson(result.response)}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - History & Actions */}
          <div className="space-y-6">
            {/* Deploy CTA */}
            {selectedConnector && (
              <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-white mb-2">Ready to deploy?</h3>
                <p className="text-sm text-slate-400 mb-4">
                  Deploy this connector to your AI agent with one click.
                </p>
                <Link to={`/connector/${selectedConnector.id}`}>
                  <Button className="w-full bg-primary hover:bg-primary/90">
                    Deploy {selectedConnector.name}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}

            {/* Test History */}
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-white mb-4">Test History</h3>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500">No tests run yet</p>
              ) : (
                <div className="space-y-3">
                  {history.map((item, index) => (
                    <div 
                      key={index}
                      className="p-3 bg-secondary/50 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-white">{item.connector}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 truncate">"{item.prompt}"</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available Actions */}
            {selectedConnector?.supported_actions?.length > 0 && (
              <div className="bg-card border border-white/5 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-white mb-4">Available Actions</h3>
                <div className="space-y-2">
                  {selectedConnector.supported_actions.map((action, index) => (
                    <div 
                      key={index}
                      className="p-3 bg-secondary/50 rounded-lg cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => setPrompt(action.example_prompt)}
                    >
                      <code className="text-sm text-primary font-mono">{action.name}</code>
                      <p className="text-xs text-slate-500 mt-1">{action.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SandboxPage;
